import { useState, useEffect } from "react";

import { useQuery } from "@tanstack/react-query";

import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import type { RuleWithField } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/types";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

import { useWebsiteKeywordsEndpoints } from "../websiteKeywordsEndpoints";
import type {
    IData,
    IWebsiteKeywordsTransformDataReturn,
} from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";
import { transformData } from "../WebsiteKeywordsPageUtillities2";

interface IUseWebsiteKeywordsDataResponse {
    data: IWebsiteKeywordsTransformDataReturn;
    isLoading: boolean;
    isError: boolean;
    dataTimeout: boolean;
}

export const useWebsiteKeywordsData = (
    paramsAsString: string,
    requestBody: RuleWithField[],
): IUseWebsiteKeywordsDataResponse => {
    const { duration, key, country, webSource } = useSearchAdvertisingDataAndParams();
    const endpoints = useWebsiteKeywordsEndpoints();
    const [dataTimeout, setDataTimeout] = useState<boolean>(false);
    const numberOfMonths = DurationService.getMonthsFromDuration(duration);

    const { data, isLoading, isError } = useQuery({
        queryKey: [endpoints.table, paramsAsString, requestBody],
        queryFn: async () =>
            DefaultFetchService.getInstance().post<IData>(`${endpoints.table}?${paramsAsString}`, requestBody),
        select: (response: IData) => transformData(response, duration),
    });

    useEffect(() => {
        if (isError && !dataTimeout) {
            setDataTimeout(true);
            const domains = key.split(",") || [];
            allTrackers.dispatchTrackEvent("Timeout notification", "open", "no-results", {
                domains: key,
                numberOfDomains: domains.length,
                numberOfMonths: numberOfMonths,
                failedApi: endpoints.table,
                filters: `Country: ${country}, Duration: ${duration}, Device: ${webSource}`,
            });
        }
    }, [isError, dataTimeout, key, country, duration, webSource, numberOfMonths, endpoints.table]);

    return { data, isLoading, isError, dataTimeout };
};
